<script setup>
// Sales Projects presentation shell — header + tab bar (reuses the same
// ProjectWorkspaceLayout chrome as the admin workspace) with one child tab
// component per pane. Every tab fetches from the existing, already-scoped
// nexceo/projects(/:id/documents|/:id/inventory) endpoints — no new backend
// endpoints, no duplicate project/document/inventory records.
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import nexceoProjectsAPI from 'dashboard/api/nexceo/projects';
import ProjectWorkspaceLayout from '../projects/ProjectWorkspaceLayout.vue';
import { salesProjectTabs, SALES_PROJECT_TABS } from './salesProjectTabs';
import OverviewTab from './tabs/OverviewTab.vue';
import DocumentsTab from './tabs/DocumentsTab.vue';
import InventoryTab from './tabs/InventoryTab.vue';
import MediaTab from './tabs/MediaTab.vue';
import MapsTab from './tabs/MapsTab.vue';
import ImmersiveTab from './tabs/ImmersiveTab.vue';

const route = useRoute();
const router = useRouter();
const { accountId, accountScopedRoute } = useAccount();

const projectId = computed(() => Number(route.params.id));
const validTabKeys = SALES_PROJECT_TABS.map(t => t.key);
const activeTabKey = computed(() =>
  validTabKeys.includes(route.params.tab) ? route.params.tab : 'overview'
);

const workspaceTabs = computed(() =>
  salesProjectTabs({ id: route.params.id, accountId: accountId.value })
);

const TAB_COMPONENTS = {
  overview: OverviewTab,
  documents: DocumentsTab,
  inventory: InventoryTab,
  media: MediaTab,
  maps: MapsTab,
  immersive: ImmersiveTab,
};
const activeComponent = computed(() => TAB_COMPONENTS[activeTabKey.value]);

const project = ref(null);
const isLoading = ref(true);
const error = ref(null);

async function loadProject(id) {
  if (!id) return;
  isLoading.value = true;
  error.value = null;
  try {
    const { data } = await nexceoProjectsAPI.show(id);
    project.value = data;
  } catch (e) {
    // 403/404 — the backend already denied access (Nexceo::AccessPolicy
    // #can_access_project?, enforced server-side regardless of what the
    // sidebar/URL shows) or the project doesn't exist.
    error.value =
      e?.response?.status === 403
        ? "You don't have access to this project."
        : 'Project not found.';
  } finally {
    isLoading.value = false;
  }
}

watch(projectId, id => loadProject(id));
onMounted(() => loadProject(projectId.value));

// If the URL carries an unknown tab key, normalize it to overview without
// a full reload.
watch(
  () => route.params.tab,
  tab => {
    if (tab && !validTabKeys.includes(tab)) {
      router.replace(
        accountScopedRoute('nexceo_sales_project_show', {
          id: route.params.id,
          tab: 'overview',
        })
      );
    }
  },
  { immediate: true }
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div
    v-if="error"
    class="flex items-center justify-center w-full h-full bg-n-background"
  >
    <div class="text-center max-w-sm">
      <span
        class="i-lucide-shield-alert size-8 text-red-500 mx-auto block mb-2"
      />
      <p class="text-sm text-n-slate-11">{{ error }}</p>
      <button
        class="mt-3 text-sm text-woot-500 hover:underline"
        @click="router.push(accountScopedRoute('nexceo_sales_projects_index'))"
      >
        Back to Projects
      </button>
    </div>
  </div>
  <ProjectWorkspaceLayout
    v-else
    :project="project"
    :tabs="workspaceTabs"
    :active-tab-key="activeTabKey"
    :back-to="{ name: 'nexceo_sales_projects_index' }"
    icon="i-lucide-building-2"
  >
    <component
      :is="activeComponent"
      v-if="!isLoading && project"
      :project="project"
      :project-id="projectId"
    />
    <div v-else class="flex items-center justify-center flex-1">
      <span class="i-lucide-loader-2 size-6 animate-spin text-woot-500" />
    </div>
  </ProjectWorkspaceLayout>
</template>
